import { useEffect, useRef, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setAllSubscriptions,
  setSubscriptionLoading,
  setSubscriptionError,
  clearSubscriptionData,
} from '../store/subscription';
import paymentAPI from '../pages/deprecated/payment/api';
import User from 'src/models/User';

interface UseSubscriptionReturn {
  isSubscriptionLoading: boolean;
  subscriptionError: string | null;
  refetchSubscriptions: () => void;
}

export const useSubscription = (
  user: User | undefined,
  shouldRefetch?: boolean
): UseSubscriptionReturn => {
  const dispatch = useAppDispatch();
  const { isLoading: isSubscriptionLoading, error: subscriptionError } =
    useAppSelector(state => state.subscription);

  const hasFetchedRef = useRef(false);
  const shouldRefetchRef = useRef(false);

  const [isSubscriptionFetching, setIsSubscriptionFetching] = useState(false);

  const fetchSubscriptionData = useCallback(async (): Promise<void> => {
    if (!user || user?.payment_provider !== 'Stripe') {
      return;
    }

    setIsSubscriptionFetching(true);
    dispatch(setSubscriptionLoading(true));
    dispatch(clearSubscriptionData()); // Clear any previous data/errors

    try {
      const subscriptions = await paymentAPI.get(
        'stripe/customer/subscriptions'
      );
      if (subscriptions?.data) {
        dispatch(setAllSubscriptions(subscriptions.data));
      }
    } catch (error) {
      dispatch(setSubscriptionError('Failed to fetch subscription data'));
      console.error('Error fetching subscriptions:', error);
    } finally {
      setIsSubscriptionFetching(false);
      dispatch(setSubscriptionLoading(false));
    }
  }, [user, dispatch]);

  const refetchSubscriptions = useCallback(() => {
    shouldRefetchRef.current = true;
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  useEffect(() => {
    // Only fetch if we haven't fetched before, shouldRefetch is true, or shouldRefetchRef is set
    if (!hasFetchedRef.current || shouldRefetch || shouldRefetchRef.current) {
      fetchSubscriptionData();
      hasFetchedRef.current = true;
      shouldRefetchRef.current = false;
    }
  }, [fetchSubscriptionData, shouldRefetch]);

  return {
    isSubscriptionLoading: isSubscriptionFetching || isSubscriptionLoading,
    subscriptionError,
    refetchSubscriptions,
  };
};
